import React, { useState, useEffect, useMemo } from "react"
import { Container, Button, Card, CardBody, Col, Row, Label } from "reactstrap"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import TableContainer from "../../components/Common/TableContainer"
import { useNavigate } from "react-router-dom"
import { getSurveys, deleteSurvey } from "api/survey"
import { SurveyReportSummary } from "api/reports"
import { exportExcel } from "api/crud"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import { useRole } from "hooks/useRole"
import SurveyChart from "./SurveyChart"
import SurveyDonutChart from "./SurveyDonutChart"
import Swal from "sweetalert2"

const SurveyList = () => {
  React.useEffect(() => {
    document.title = "Surveys | CA Portal"
  }, [])

  const { isAdmin, isEditor, isAnalyst, isSuperAdmin } = useRole()

  const navigate = useNavigate()

  const [surveysData, setSurveysData] = useState([])
  const [loading, setIsLoading] = useState(false)
  const [surveyReportData, setSurveyReportData] = useState([])
  const [startDate, setStartDate] = useState(new Date())
  const [endDate, setEndDate] = useState(new Date())
  const [otherParams, setOtherParams] = useState({
    searchColumns: [],
    pageSize: 100,
    pageNumber: 0,
    sortBy: "string",
    ascendingSort: true,
  })

  useEffect(() => {
    const currentDate = new Date()
    const firstDayOfYear = new Date(currentDate.getFullYear(), 0, 1)
    const lastDayOfYear = new Date(currentDate.getFullYear(), 11, 31)
    const initialStartDate = formatDate(firstDayOfYear)
    const initialEndDate = formatDate(lastDayOfYear)

    setStartDate(firstDayOfYear)
    setEndDate(lastDayOfYear)

    fetchSurveyData({
      fromDate: initialStartDate,
      toDate: initialEndDate,
      ...otherParams,
    })
    fetchReportData({
      fromDate: initialStartDate,
      toDate: initialEndDate,
      ...otherParams,
    })
  }, [])

  const fetchSurveyData = async params => {
    setIsLoading(true)
    try {
      const surveyRes = await getSurveys(params)
      setSurveysData(surveyRes?.data)
      setIsLoading(false)
    } catch (error) {
      console.error("Error fetching surveys data:", error)
      setIsLoading(false)
    }
  }

  const fetchReportData = async params => {
    try {
      const reportRes = await SurveyReportSummary(params)
      setSurveyReportData(reportRes?.data)
    } catch (error) {
      console.error("Error fetching report data:", error)
    }
  }

  const handleApply = () => {
    const params = {
      fromDate: formatDate(startDate),
      toDate: formatDate(endDate),
      ...otherParams,
    }
    fetchSurveyData(params)
    fetchReportData(params)
  }

  const formatDate = date => {
    if (!(date instanceof Date)) {
      date = new Date(date)
    }
    if (!(date instanceof Date) || isNaN(date.getDate())) {
      return ""
    }
    const day = String(date.getDate()).padStart(2, "0")
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const year = date.getFullYear()
    return `${day}-${month}-${year}`
  }

  const handleStartDateChange = date => {
    setStartDate(date)
  }

  const handleEndDateChange = date => {
    setEndDate(date)
  }

  const columns = useMemo(
    () => [
      {
        Header: "#ID",
        accessor: "id",
      },
      {
        Header: "Title",
        accessor: "title",
      },
      // {
      //   Header: "Survey Type",
      //   accessor: "type",
      // },
      {
        Header: "STATUS",
        accessor: "status",
      },
      {
        Header: "Date Created",
        accessor: "createdAt",
        Cell: ({ value }) => {
          const formattedDate = new Date(value).toLocaleString(undefined, {
            month: "numeric",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "numeric",
            hour12: true,
          })
          return formattedDate
        },
      },
    ],
    []
  )

  const handleViewDetails = record => {
    navigate(`/survey/${record.original.id}`)
  }

  const handleCreate = () => {
    navigate("/survey")
  }

  const onEdit = record => {
    navigate(`${"/editsurvey"}/${record.original.id}/edit`)
  }

  const handleDelete = async id => {
    try {
      await deleteSurvey(id)
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Survey deleted successfully.",
      })
      fetchSurveyData({
        fromDate: formatDate(startDate),
        toDate: formatDate(endDate),
        ...otherParams,
      })
    } catch (error) {
      console.error("Error deleting survey:", error)
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error deleting survey. Please try again.",
      })
    }
  }

  const handleExport = async () => {
    try {
      const params = {
        fromDate: formatDate(startDate),
        toDate: formatDate(endDate),
        ...otherParams,
      }

      await exportExcel("survey", params)
    } catch (error) {
      console.error("Error exporting surveys:", error)
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error exporting surveys. Please try again.",
      })
    }
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Surveys" breadcrumbItem="View Surveys" />

        {/* Filter Row */}
        <Row className="mb-4 align-items-end">
          <Col md={3}>
            <Label className="form-label">Start Date</Label>
            <DatePicker
              selected={startDate}
              onChange={handleStartDateChange}
              className="form-control"
            />
          </Col>

          <Col md={3}>
            <Label className="form-label">End Date</Label>
            <DatePicker
              selected={endDate}
              onChange={handleEndDateChange}
              className="form-control"
            />
          </Col>

          <Col md={3} className="d-flex">
            <Button
              color="primary"
              className="me-2 flex-grow-1"
              onClick={handleApply}
            >
              Apply Filter
            </Button>
            <Button
              color="success"
              onClick={handleExport}
              className="me-2 flex-grow-1"
            >
              Export Surveys
            </Button>
          </Col>
        </Row>

        {(isAdmin || isSuperAdmin || isAnalyst) && (
          <Row className="mb-4">
            <Col md={6}>
              <Card>
                <CardBody>
                  {surveyReportData && surveyReportData.length > 0 ? (
                    <SurveyChart
                      dataColors='["--bs-success"]'
                      data={surveyReportData}
                    />
                  ) : (
                    <div className="text-center text-muted">
                      No available data for selected months
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>

            <Col md={6}>
              <Card>
                <CardBody>
                  {surveyReportData && surveyReportData.length > 0 ? (
                    <SurveyDonutChart
                      dataColors='["--bs-warning","--bs-secondary", "--bs-primary", "--bs-danger", "--bs-success"]'
                      data={surveyReportData}
                    />
                  ) : (
                    <div className="text-center text-muted">
                      No available data for selected months
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
        <TableContainer
          showView
          hasCreate={isAdmin || isSuperAdmin || isEditor}
          showEdit={isAdmin || isSuperAdmin || isEditor}
          showDelete={isAdmin || isSuperAdmin}
          columns={columns}
          data={surveysData || []}
          isGlobalFilter={true}
          isAddOptions={false}
          customPageSize={10}
          isPagination={true}
          iscustomPageSizeOptions={true}
          isLoading={loading}
          loadingRows={3}
          loadingText="Loading surveys..."
          emptyMessage="No surveys found"
          addText="Create New Survey"
          tableClass="align-middle table-nowrap table-check table"
          theadClass="table-light"
          paginationDiv="col-12"
          pagination="justify-content-left pagination pagination-rounded"
          handleEdit={onEdit}
          handleView={handleViewDetails}
          handleCreate={handleCreate}
          handleDelete={handleDelete}
          fetchData={handleApply}
        />
      </Container>
    </div>
  )
}

export default SurveyList
